/**
 * Decide whether a ticket placement should REUSE an existing vehicle or CREATE
 * a new one.
 *
 * K19-2 (data integrity): `CreateTicket` always passes BOTH a `vehicle` object
 * (year/make/model — the form is prefilled even when a returning customer's
 * existing vehicle is selected) AND `vehicleId` (the selected existing vehicle).
 * The OptiPlace main-ticket path previously branched on `vehicle.year` FIRST and
 * called `createVehicle(...)`, ignoring `vehicleId` — so every returning-customer
 * ticket orphaned the selected vehicle and accumulated duplicate VIN history.
 *
 * The correct precedence (already used by the split-ticket path,
 * OptiPlaceModal.tsx:1055-1067) is: an existing `vehicleId` wins; only create a
 * new vehicle when there is no `vehicleId`. This pure helper makes that decision
 * unit-testable.
 */
export interface TicketVehicleInput {
  /** The selected existing vehicle's id, when a returning customer/vehicle was chosen. */
  vehicleId?: string | null;
  /** The form's vehicle object (year/make/model), present for new + prefilled selections. */
  vehicle?: { year?: unknown } | null;
}

export type TicketVehicleAction =
  /** Reuse the already-selected existing vehicle — do NOT create a duplicate. */
  | { action: 'reuse'; vehicleId: string }
  /** No existing vehicle: create a new one from the form's vehicle object. */
  | { action: 'create' }
  /** Neither an existing id nor enough form data to create — caller should error. */
  | { action: 'none' };

export function decideTicketVehicleAction(input: TicketVehicleInput): TicketVehicleAction {
  // Existing vehicle ALWAYS wins, even when `vehicle.year` is also present.
  if (input.vehicleId) {
    return { action: 'reuse', vehicleId: input.vehicleId };
  }
  // No existing vehicle → create only when we have at least a year to create from.
  if (input.vehicle && input.vehicle.year != null && input.vehicle.year !== '') {
    return { action: 'create' };
  }
  return { action: 'none' };
}
